import React, { useRef, useEffect } from 'react';
import { Box, TextField } from '@mui/material';

const OTP_LENGTH = 6;

export default function OtpInput({ value = '', onChange, disabled = false }) {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!disabled && value.length < OTP_LENGTH) {
      inputRefs.current[value.length]?.focus();
    }
  }, [disabled, value.length]);

  const emitChange = (nextValue) => {
    const sanitized = nextValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
    onChange(sanitized);
  };

  const handleChange = (index, event) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1);
    const chars = value.padEnd(OTP_LENGTH, ' ').split('');
    chars[index] = digit || ' ';
    const nextValue = chars.join('').replace(/\s/g, '').slice(0, OTP_LENGTH);
    emitChange(nextValue);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const chars = value.split('');
        chars[index] = '';
        emitChange(chars.join(''));
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted) {
      emitChange(pasted);
      const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${OTP_LENGTH}, 40px)`,
        gap: '8px',
        justifyContent: 'center',
        width: 'fit-content',
        mx: 'auto',
        my: 2,
      }}
    >
      {Array.from({ length: OTP_LENGTH }).map((_, index) => (
        <TextField
          key={index}
          fullWidth={false}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          value={value[index] ?? ''}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          disabled={disabled}
          variant="outlined"
          size="small"
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            'aria-label': `OTP digit ${index + 1}`,
            style: {
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 600,
              padding: '10px 0',
              height: '40px',
              boxSizing: 'border-box',
            },
          }}
          sx={{
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            m: 0,
            '& .MuiOutlinedInput-root': {
              width: 40,
              height: 40,
              borderRadius: '6px',
              bgcolor: 'rgba(255,255,255,0.06)',
              color: '#fff',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
              '&.Mui-focused fieldset': { borderColor: '#5b9dff', borderWidth: '1px' },
            },
            '& .MuiOutlinedInput-input': {
              p: 0,
              textAlign: 'center',
            },
          }}
        />
      ))}
    </Box>
  );
}
